import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SupportService, ReportTicketResult } from './support.service';
import { DiagnosticsService } from './diagnostics.service';

/**
 * Botón "Reportar un problema" en el header: captura la pantalla, pide
 * título/descripción y crea un ticket en la mesa de ayuda, devolviendo un enlace
 * al portal para completar/anotar.
 *
 * Captura de pantalla — dos vías:
 *  1) NATIVA (getDisplayMedia): la compone la GPU, sin recorrer el DOM, por lo que
 *     NUNCA congela el navegador (a diferencia de html2canvas/snapdom, que clonan
 *     todo el árbol y se traban en dashboards pesados). Es la opción recomendada.
 *     Pide un permiso al usuario para compartir la pestaña.
 *  2) RÁPIDA SIN PERMISO (html2canvas, carga diferida): no muestra diálogo, pero en
 *     pantallas muy grandes puede tardar. Se usa como alternativa y como respaldo
 *     en navegadores sin captura nativa.
 *
 * El modal NO captura al abrirse: así se evita el congelamiento inicial "en seco".
 */
@Component({
  selector: 'app-support-widget',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  templateUrl: './support-widget.component.html',
  styleUrls: ['./support-widget.component.scss'],
})
export class SupportWidgetComponent {
  private readonly svc = inject(SupportService);
  private readonly diag = inject(DiagnosticsService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  open = false;
  capturing = false;
  sending = false;
  step: 'form' | 'done' = 'form';

  /** Máximo de capturas por reporte (evita cuerpos enormes). */
  readonly maxShots = 5;

  subject = '';
  description = '';
  screenshots: string[] = [];
  error = '';
  result: ReportTicketResult | null = null;

  /** Indica si el navegador soporta la captura nativa (getDisplayMedia). */
  get supportsNative(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;
  }

  get canAddMore(): boolean {
    return this.screenshots.length < this.maxShots;
  }

  /** Abre el modal SIN capturar (evita el congelamiento inicial). */
  launch(): void {
    this.error = '';
    this.result = null;
    this.step = 'form';
    this.subject = '';
    this.description = '';
    this.screenshots = [];
    this.open = true;
    this.cdr.markForCheck();
  }

  close(): void {
    this.open = false;
    this.screenshots = [];
    this.cdr.markForCheck();
  }

  /**
   * Captura recomendada (nativa). Oculta el modal, toma un frame del compositor
   * del navegador y agrega la imagen. Si el navegador no soporta la API nativa,
   * cae automáticamente a la captura rápida (html2canvas).
   */
  async captureNative(): Promise<void> {
    if (!this._guardMax()) return;
    if (!this.supportsNative) return this.captureFast();
    await this._withHiddenModal(() => this._captureNative());
  }

  /** Captura rápida sin permiso (html2canvas, carga diferida). */
  async captureFast(): Promise<void> {
    if (!this._guardMax()) return;
    await this._withHiddenModal(() => this._captureHtml2Canvas());
  }

  removeShot(index: number): void {
    this.screenshots = this.screenshots.filter((_, i) => i !== index);
    this.cdr.markForCheck();
  }

  submit(): void {
    if (this.subject.trim().length < 4) {
      this.error = 'Describe brevemente el problema en el título.';
      return;
    }
    this.sending = true;
    this.error = '';
    this.svc
      .reportProblem({
        subject: this.subject.trim(),
        description: this.description.trim(),
        screen: this.router.url,
        screenshots: this.screenshots.length ? this.screenshots : undefined,
        diagnostics: this._safeSnapshot(),
      })
      .subscribe({
        next: (r) => {
          this.sending = false;
          this.result = r;
          this.step = 'done';
          this.cdr.markForCheck();
        },
        error: (e) => {
          this.sending = false;
          this.error = e?.error?.message ?? 'No se pudo enviar el reporte. Intenta de nuevo.';
          this.cdr.markForCheck();
        },
      });
  }

  // ── Interno ────────────────────────────────────────────────────────────────

  private _guardMax(): boolean {
    if (this.screenshots.length >= this.maxShots) {
      this.error = `Puedes adjuntar hasta ${this.maxShots} capturas.`;
      this.cdr.markForCheck();
      return false;
    }
    this.error = '';
    return true;
  }

  /** Oculta el modal, ejecuta la captura y vuelve a mostrarlo. */
  private async _withHiddenModal(capture: () => Promise<string | null>): Promise<void> {
    this.open = false;
    this.capturing = true;
    this.cdr.markForCheck();
    // Deja repintar sin el modal antes de capturar.
    await new Promise((r) => setTimeout(r, 150));
    try {
      const shot = await capture();
      if (shot) this.screenshots = [...this.screenshots, shot];
    } catch {
      /* usuario canceló el permiso o falló la captura — sin ruido */
    } finally {
      this.capturing = false;
      this.open = true;
      this.cdr.markForCheck();
    }
  }

  private _safeSnapshot(): string | undefined {
    try {
      return this.diag.snapshot();
    } catch {
      return undefined;
    }
  }

  /** Captura rápida con html2canvas (carga diferida), recortada al viewport. */
  private async _captureHtml2Canvas(): Promise<string | null> {
    const html2canvas = (await import('html2canvas')).default;
    const canvas = await html2canvas(document.body, {
      logging: false,
      useCORS: true,
      backgroundColor: '#ffffff',
      // scale 1: html2canvas es costoso; el x2 no aporta y encarece la captura.
      scale: 1,
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight,
      x: window.scrollX,
      y: window.scrollY,
      width: document.documentElement.clientWidth,
      height: document.documentElement.clientHeight,
    });
    return canvas.toDataURL('image/png');
  }

  /** Captura nativa (getDisplayMedia): un frame del compositor del navegador. */
  private async _captureNative(): Promise<string | null> {
    const md = navigator.mediaDevices as MediaDevices & {
      getDisplayMedia?: (c?: unknown) => Promise<MediaStream>;
    };
    if (!md?.getDisplayMedia) return null;
    const stream = await md.getDisplayMedia({
      video: { displaySurface: 'browser' },
      audio: false,
      // preferCurrentTab (Chrome) evita el picker completo y ofrece "esta pestaña".
      preferCurrentTab: true,
    });
    const track = stream.getVideoTracks()[0];
    try {
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await video.play().catch(() => undefined);
      if (!video.videoWidth) {
        await new Promise<void>((res) => {
          video.onloadedmetadata = () => res();
          setTimeout(res, 1500);
        });
      }
      await new Promise((r) => requestAnimationFrame(r));
      const c = document.createElement('canvas');
      c.width = video.videoWidth || 1280;
      c.height = video.videoHeight || 720;
      const ctx = c.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(video, 0, 0, c.width, c.height);
      video.srcObject = null;
      return c.toDataURL('image/png');
    } finally {
      track?.stop();
    }
  }
}
